import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ExternalLink,
  Radio,
  ScanSearch,
  Shield,
  Upload,
  Workflow,
} from 'lucide-react';

const ENCODER_DEMO_URL = '/harmony-encoder';

const HIGHLIGHTS = [
  {
    icon: Upload,
    title: 'Encode media',
    text: 'Music, video, and livestream content — choose static or dynamic workflow activation and embed secure signals.',
  },
  {
    icon: ScanSearch,
    title: 'Inspect before deploy',
    text: 'Verify activation signals in the browser before anything goes live. Encode and inspect only — no auto-execution here.',
  },
  {
    icon: Shield,
    title: 'Permission-gated execution',
    text: 'Workflows run only on authorized Harmony Nodes under approved runtime policies — with proof receipts and opt-in listener nodes.',
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45 },
};

export default function AuraResonantLayerPage() {
  return (
    <>
      <Helmet>
        <title>AuRA Resonant Layer — SELF Labs</title>
        <meta
          name="description"
          content="AuRA Resonant Layer turns media into secure activation points for permission-gated AI workflows. Try Harmony Encoder Studio."
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white">
        <div
          className="pointer-events-none fixed inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, var(--glow-purple) 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 15% 75%, var(--glow-orange) 0%, transparent 45%), radial-gradient(ellipse 50% 40% at 85% 65%, var(--glow-blue) 0%, transparent 45%)',
          }}
        />

        {/* Hero */}
        <section className="relative px-4 pb-16 pt-28 sm:px-6 lg:px-8">
          <div className="relative mx-auto max-w-3xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-white/80">
                <Radio className="h-3.5 w-3.5" />
                AuRA Resonant Layer
              </span>
              <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
                Sound-based signals for AI workflows
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/65">
                AuRA is SELF Labs&apos; resonant middleware — an ethical layer between people and AI
                that turns media into permission-gated activation points instead of opaque data grabs.
              </p>
              <p className="mx-auto mt-4 max-w-xl text-sm text-white/45">
                Fair data ownership, transparent execution, and value that flows back to creators and
                listeners.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Harmony Encoder Studio */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <motion.div {...fadeUp} className="rounded-2xl border border-white/12 bg-white/[0.03] p-8 sm:p-10">
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
                Live demo
              </p>
              <h2 className="mt-3 text-2xl font-bold sm:text-3xl">Harmony Encoder Studio</h2>
              <p className="mt-1 text-sm text-white/50">
                Permission-gated AI workflows through secure media signals
              </p>
              <p className="mt-6 text-white/65 leading-relaxed">
                The first sound-based data center for AI workflows. Harmony transforms music, video,
                and livestreams into secure activation points — upload content, pick a workflow
                activation method, encode it, then inspect the file to verify the signal before
                deployment.
              </p>
              <p className="mt-4 text-sm text-white/45">
                The browser encodes and inspects only. No automatic execution, payments, or external
                AI APIs in the studio — execution stays on authorized Harmony Nodes under approved
                runtime policies.
              </p>

              <ul className="mt-8 space-y-3 text-sm text-white/60">
                {[
                  'Encode music, video, and livestream content',
                  'Static or dynamic workflow activation',
                  'Inspect encoded media before deployment',
                  'Built for opt-in listener nodes and proof receipts',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <Workflow className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to={ENCODER_DEMO_URL}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-black shadow-[0_0_24px_var(--glow-purple),0_0_32px_var(--glow-blue)] hover:bg-white/90 transition-transform hover:scale-[1.02]"
                >
                  Try Harmony Encoder Studio
                  <ExternalLink className="h-4 w-4" />
                </Link>
                <Link
                  to={ENCODER_DEMO_URL}
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:border-white/35 hover:bg-white/10 transition-transform hover:scale-[1.02]"
                >
                  Request a demo
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Highlights */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl">
            <motion.div {...fadeUp} className="text-center">
              <h2 className="text-2xl font-bold">Harmony turns content into infrastructure</h2>
              <p className="mx-auto mt-3 max-w-xl text-white/55">
                Creators, brands, and platforms prepare media with secure activation signals — then
                listeners and nodes opt in to permission-gated AI workflows.
              </p>
            </motion.div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {HIGHLIGHTS.map((item, i) => (
                <motion.div
                  key={item.title}
                  {...fadeUp}
                  transition={{ delay: i * 0.06 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                >
                  <item.icon className="mb-4 h-7 w-7 text-white/70" strokeWidth={1.5} />
                  <h3 className="font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/55">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Footer note */}
        <section className="border-t border-white/10 px-4 py-16 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
            <p className="text-white/55">
              For the unified AI API and model mesh, see{' '}
              <a href="/resonatia" className="text-white/75 underline underline-offset-2 hover:text-white">
                Resonatia
              </a>
              . For photorealistic talking avatars, see{' '}
              <a href="/role-model-ai" className="text-white/75 underline underline-offset-2 hover:text-white">
                Role Model AI
              </a>
              .
            </p>
            <Link
              to={ENCODER_DEMO_URL}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-transform hover:scale-[1.02]"
            >
              Open Harmony Encoder Studio
              <ExternalLink className="h-4 w-4" />
            </Link>
          </motion.div>
        </section>
      </div>
    </>
  );
}
